import { useEffect, useState, memo, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'

import Header from 'components/Shared/Header'
import Loader from 'components/Shared/Loader'
import {
  setAccessToken,
  setDisplayParams,
  setFilterParams,
  setPassword,
  setChipsVisibility,
} from 'redux/slices/StakeholderDashboard'
import stakeHolderDetailsAPI from 'api/ApiActions/Reporting/StakeholderDashboard/StakeHolder'
import { activityOverTimeSwitcher } from 'containers/Reporting/Shared/Constants'
import ReportingDashboardFilter from 'containers/Reporting/Shared/Filters/ReportingDashboardFilter'
import GraphicalDetailsComp from 'components/Reporting/Overview/GraphicalDetails'
import {
  useKeyMetricsDetails,
  useActivityOverTime,
  useVisitorEngagement,
  useTopPerformingTools,
  useContributedTags,
  useContributedLocations,
} from 'containers/Reporting/Shared/CardContainer/index'
import { ActivityFilterSelection } from 'containers/Reporting/Shared/Types'
import usePrevious from 'lib/hooks/UsePrevious'
import { useAppSelector } from 'redux/Hook'
import { setFilterDropdown } from '../Shared/Filters/ActivityOverTimeHelper'
import { SelectDropdown as SelectDropdownType } from 'types/SelectDropdown'
import NotFound from 'components/Shared/ErrorPages/404'
import ShowHideButton from 'components/Shared/ShowHideButton'
import ColorsTheme from 'assets/styles/themes/ColorTheme'

const StakeholderDashboard = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const location = useLocation()

  const {
    features: {
      features: {
        dashboard_filters_visibility: { enabled: filterVisibilty = false } = {},
      },
    },
    filterBuilder: { selectedFilters },
    stakeholderDashboardDetails: {
      isAuthTokenValidated = false,
      accessToken = '',
      password: stakeholderPassword,
    },
    site: {
      site: { name: siteName = '', id: site_id = '' },
    },
    stakeholderDashboardDetails: { displayParams = {}, areChipsHidden = false },
  }: any = useAppSelector((state) => state)

  const dateRange =
    selectedFilters.filter((f: any) => f.name === 'dateRange')[0]?.dateRange ||
    {}
  const prevProps = usePrevious({ selectedFilters })

  const access_token = location.pathname.split('pv/').pop()

  const [activityFilterOptions, setActivityFilterOptions] =
    useState<SelectDropdownType>([])
  const [activityFilterSelection, setPeriodFilter] =
    useState<ActivityFilterSelection>({
      period: 'daily',
    }) // object used to get immediate state update, with prevProps, with string update states are not updating as expected
  const [pageAccess, setPageAcess] = useState<boolean>(false)
  const { keyMetrics, isKeyMetricsLoading } = useKeyMetricsDetails()
  const [notFound, setNotFound] = useState<boolean>(false)
  const { activityData, isActivityLoading } = useActivityOverTime(
    activityFilterSelection,
  )

  const { visitorEngagementData, isVisitorEngagementLoading } =
    useVisitorEngagement()

  const { topPerformingToolsData, isTopPerformingToolsLoading } =
    useTopPerformingTools()

  const { contributionTagsData, isContributionTagsLoading } =
    useContributedTags()

  const { contributionsLocationData, isContributionsLocationLoading } =
    useContributedLocations()

  const getUserSignInActivity = async () => {
    const response = await (site_id &&
      stakeHolderDetailsAPI.fetchSignInActivity({
        access_token,
        site_id,
      }))
    if (response.data) {
      const {
        data: {
          data: { passwordEnabled },
        },
      }: any = response
      await getFilterParams(passwordEnabled)
      dispatch(setAccessToken(access_token))
      setActivityFilterOptions(activityOverTimeSwitcher)
    } else if (response.response.status === 404) {
      setNotFound(true)
    }
  }

  const getParams = async (passwordEnabled?: boolean) => {
    let params: { access_token: any; password?: any } = { access_token }
    let password: any
    if (passwordEnabled) {
      password = await prompt(t('reporting.stakeHolder.enterPassword'))
      params = { access_token, password: `${password}` }
      dispatch(setPassword(password))
    }

    return params
  }

  const showErrorMessage = (response: any) => {
    const errMessage = JSON.parse(response?.request?.response)?.errors?.message
    !!stakeholderPassword && alert(errMessage)
    setPageAcess(false)
  }

  const showDashboard = useCallback(async (response: any) => {
    const {
      data: {
        data: { filterParams = [], displayParams: displayParamsData = {} } = {},
      } = {},
    } = await response

    await dispatch(setFilterParams(filterParams))
    await dispatch(setDisplayParams(displayParamsData))

    setPageAcess(true)
  }, [])

  const getFilterParams = async (passwordEnabled: boolean) => {
    const params = await getParams(passwordEnabled)
    const response = await stakeHolderDetailsAPI.fetchFilterParams(params)

    if (response?.response?.status === 401) {
      showErrorMessage(response)
    } else {
      showDashboard(response)
    }
  }

  const onSetActivityFilterDropdown = async (options: string) => {
    await setPeriodFilter((prevState: any) => ({
      ...prevState.activityOverTimeSelection,
      period: options,
    }))
  }
  const chipsLength = Object.keys(displayParams).length
  const getCustomBadge = () => {
    if (!filterVisibilty) {
      return (
        <Box sx={{ marginBottom: '1.5rem' }}>
          <Header title={siteName} />
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: { xs: 'inline', md: 'flex' },
          marginBottom: { xs: 0, md: '1.5rem' },
        }}
        justifyContent="space-between"
        width="inherit"
        alignItems="center"
      >
        <Box>
          <Header title={siteName} />
        </Box>

        <Box
          display="flex"
          justifyContent="center"
          alignSelf="end"
          sx={{ marginTop: { xs: '1.5rem', md: 0 } }}
        >
          <ShowHideButton
            count={chipsLength}
            disabled={!chipsLength}
            isHidden={areChipsHidden}
            onClick={() => dispatch(setChipsVisibility(!areChipsHidden))}
          />
        </Box>
      </Box>
    )
  }

  const layout: any = (
    <>
      {getCustomBadge()}
      <ReportingDashboardFilter isSharedDashboard={true}>
        <GraphicalDetailsComp
          isLoading={{
            isKeyMetricsLoading,
            isActivityLoading,
            isVisitorEngagementLoading,
            isTopPerformingToolsLoading,
            isContributionTagsLoading,
            isContributionsLocationLoading,
          }}
          keyMetrics={keyMetrics}
          activityData={activityData}
          visitorEngagementData={visitorEngagementData}
          topPerformingToolsData={topPerformingToolsData}
          contributionTagsData={contributionTagsData}
          contributionsLocationData={contributionsLocationData}
          onSetActivityFilterDropdown={onSetActivityFilterDropdown}
          activityOverTimeOptions={activityOverTimeSwitcher}
          activityOverTimeSelection={activityFilterSelection}
        />
      </ReportingDashboardFilter>
    </>
  )

  useEffect(() => {
    if (
      prevProps &&
      JSON.stringify(prevProps?.selectedFilters) !==
        JSON.stringify(selectedFilters)
    ) {
      const period = setFilterDropdown({
        option: 'monthly',
        dateRange,
        activityFilterOptions,
      })
      period &&
        setPeriodFilter((prevState: any) => ({
          ...prevState.activityOverTimeSelection,
          period,
        }))
    }
  }, [selectedFilters])

  useEffect(() => {
    const getAutherised = async () => {
      if (isAuthTokenValidated && site_id) {
        await getUserSignInActivity()
      }
    }
    getAutherised()
  }, [isAuthTokenValidated, site_id])

  const getLayout = () => (pageAccess ? layout : <NotFound />)

  return accessToken ? getLayout() : notFound ? <NotFound /> : <Loader />
}

const WithThemeProvider = () => (
  <ThemeProvider theme={ColorsTheme}>
    <StakeholderDashboard />
  </ThemeProvider>
)

export default memo(WithThemeProvider)
